/* ═══════════════════════════════════════════════════════════════════════════
   Runtime configuration
   ───────────────────────────────────────────────────────────────────────────
   One module reads `import.meta.env`, and it validates on the way through.

   The alternative — sprinkling `import.meta.env.VITE_API_URL` across the
   codebase — fails in the worst possible way: a missing or misspelt variable
   is `undefined`, `undefined` stringifies into a URL as the literal text
   "undefined", and the app ships pointing at `https://undefined/orders`. That
   is a runtime 404 in production traced back through four layers. Parsing here
   turns the same mistake into a thrown error on the first import, which in a
   CI build is a failed build.

   Values are frozen at build time. Vite substitutes `import.meta.env.*` as
   literals during the bundle, so nothing here is parsed at runtime and there
   is no cost to reading `config` in a hot path.
   ═══════════════════════════════════════════════════════════════════════════ */

export type ApiMode = 'mock' | 'http'

export interface AppConfig {
  /** Which of the three front ends this is.
   *
   *  Not derived from the bundle or the hostname: `lib/telemetry.ts` is copied
   *  verbatim between the three repos and this is the one value it reads to
   *  tell them apart, so it has to be a constant somebody can see. */
  app: 'customer' | 'partner' | 'admin'
  /** Which implementation of the API contract is live. */
  apiMode: ApiMode
  /** Prefix for every HTTP route. No trailing slash. */
  apiUrl: string
  /** Where the partner app lives. No trailing slash.
   *
   *  The one address this site holds for somewhere that is not itself, and it
   *  earns the place because applying to be a partner is not this site's job.
   *  It used to be: `/join/:kind` was a second, complete application form —
   *  eighteen fields, its own validation, its own copy of what each shape of
   *  partner must supply — posting to the same `POST /applications`. Two forms
   *  against one endpoint is two things to keep in step with the server, and
   *  they were not in step: only one of them ever grew the documents step, and
   *  only one of them could set the password that lets an applicant come back
   *  and look. So this site keeps the page that *sells* the proposition and
   *  hands the form to the app that owns it. */
  partnerUrl: string
  /** Milliseconds before an in-flight request aborts itself. */
  apiTimeoutMs: number
  /** 0–1. Share of mock *writes* that raise a server error. */
  mockFailureRate: number
  /** Multiplier on simulated mock latency. 0 resolves immediately. */
  mockLatency: number
  /** The identity provider's web config, or null in a build that never reaches
   *  it. Public by design: it ships in the bundle, and the security boundary is
   *  the server verifying the token rather than these values being secret. */
  firebase: FirebaseConfig | null
  /** Where error reports are posted. Empty means "derive it from `apiUrl`",
   *  which is right in every deploy so far; it exists as an override for the
   *  case where reports should go somewhere the API does not. Empty *and* mock
   *  mode means reports stay in the console, which is where a developer is
   *  already looking. */
  telemetryUrl: string
  /** Whether to start Firebase Performance Monitoring. On by default in an
   *  `http` build and switchable because it installs its own `fetch` wrapper —
   *  the first thing worth ruling out when network behaviour looks strange. */
  performance: boolean
  /** Build identity, inlined by vite.config.ts. */
  version: string
  isDev: boolean
}

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  messagingSenderId: string
  appId: string
  /** The Google Analytics stream, `G-XXXXXXXXXX`.
   *
   *  Optional where the other five are required, because it does not exist
   *  until Google Analytics is linked to the Firebase project — a console
   *  action, with no API behind it. Absent, Analytics stays off and the rest of
   *  telemetry is unaffected; the reports still reach the API, which is the
   *  half that carries stack traces. */
  measurementId?: string
}

function bounded(raw: string | undefined, fallback: number, min: number, max: number, name: string): number {
  if (raw === undefined || raw === '') return fallback
  const value = Number(raw)
  if (!Number.isFinite(value)) {
    throw new Error(`[hexclean] ${name} must be a number, got ${JSON.stringify(raw)}`)
  }
  if (value < min || value > max) {
    throw new Error(`[hexclean] ${name} must be between ${min} and ${max}, got ${value}`)
  }
  return value
}

const env = import.meta.env

const apiMode: ApiMode = env.VITE_API_MODE ?? 'mock'
if (apiMode !== 'mock' && apiMode !== 'http') {
  throw new Error(`[hexclean] VITE_API_MODE must be 'mock' or 'http', got ${JSON.stringify(apiMode)}`)
}

/* Trailing slashes are stripped once, here, so `routes.ts` can be written as
   '/orders' throughout without every join worrying about a double slash. */
const apiUrl = (env.VITE_API_URL ?? '/api').replace(/\/+$/, '')

/* An HTTP build with nowhere to point is the one misconfiguration that must
   never reach a user, and it is silent otherwise — the app renders, every
   screen spins, and the failure looks like a backend outage. */
if (apiMode === 'http' && apiUrl === '') {
  throw new Error('[hexclean] VITE_API_MODE=http requires VITE_API_URL to be set')
}

/* Defaulted rather than required, and defaulted to the real host.
   `VITE_PARTNER_URL` is unset in every existing `.env` in this repo, and a
   build that threw on it would be a deploy that fails on a link. An empty
   string would be worse still — it renders as a link to this site's own root,
   which is a partner who presses Apply and lands back on the customer home
   page with no idea anything went wrong. */
const partnerUrl = ((env.VITE_PARTNER_URL ?? '').trim() || 'https://hexclean-partner.thebughex.com')
  .replace(/\/+$/, '')

/** The five keys the SDK needs. All or nothing: a partial block is a build that
 *  fails at the sign-in screen rather than at import, which is the failure this
 *  module exists to prevent. */
function firebaseConfig(mode: ApiMode): FirebaseConfig | null {
  const values = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  }
  const missing = Object.entries(values)
    .filter(([, value]) => (value ?? '').trim() === '')
    .map(([key]) => key)

  if (missing.length === Object.keys(values).length) {
    /* None set. Fine in mock mode, where the mock owns the challenge and the
       SDK is never reached. */
    if (mode === 'mock') return null
    throw new Error(
      '[hexclean] VITE_API_MODE=http requires the VITE_FIREBASE_* block — see .env.example',
    )
  }
  if (missing.length > 0) {
    throw new Error(`[hexclean] incomplete Firebase config, missing: ${missing.join(', ')}`)
  }
  /* Deliberately outside the completeness check above. The five keys are all
     or nothing because a partial identity config is a sign-in screen that
     fails; this sixth one only decides whether Analytics runs, and a build
     without it is the normal case rather than a broken one. */
  const measurementId = (env.VITE_FIREBASE_MEASUREMENT_ID ?? '').trim()

  return Object.freeze({
    ...(values as FirebaseConfig),
    ...(measurementId !== '' ? { measurementId } : {}),
  })
}

export const config: AppConfig = Object.freeze({
  app: 'customer',
  apiMode,
  apiUrl,
  partnerUrl,
  firebase: firebaseConfig(apiMode),
  telemetryUrl: (env.VITE_TELEMETRY_URL ?? '').trim().replace(/\/+$/, ''),
  performance: (env.VITE_TELEMETRY_PERFORMANCE ?? 'true') !== 'false',
  apiTimeoutMs: bounded(env.VITE_API_TIMEOUT_MS, 15_000, 1_000, 120_000, 'VITE_API_TIMEOUT_MS'),
  /* Failing ~6% of writes in development is what keeps the retry, toast and
     rollback paths honest. In a production build the default is 0 — injected
     failure is a development tool, and shipping it would be sabotage. */
  mockFailureRate: bounded(env.VITE_MOCK_FAILURE_RATE, env.DEV ? 0.06 : 0, 0, 1, 'VITE_MOCK_FAILURE_RATE'),
  mockLatency: bounded(env.VITE_MOCK_LATENCY, 1, 0, 10, 'VITE_MOCK_LATENCY'),
  version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0',
  isDev: env.DEV,
})
